import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { baseURL } from '../globalUtils/axiosConfig';

export const useWebSocket = () => {
  const socketRef = useRef(null);
  const { user } = useSelector((state) => state.authReducer);
  const token = user?.auth_token;

  const connect = useCallback(() => {
    if (!token) return null;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    const socket = io(`${baseURL}/notifications`, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;
    return socket;
  }, [token]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const emit = useCallback((event, data) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const on = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  }, []);

  const off = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.off(event, callback);
    }
  }, []);

  useEffect(() => {
    const socket = connect();
    
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [connect]);

  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }
  }, [token, connect, disconnect]);

  return {
    socket: socketRef.current,
    connect,
    disconnect,
    emit,
    on,
    off,
    isConnected: !!socketRef.current?.connected
  };
};

export default useWebSocket;
